<template>
  <div class="permissions">
    <b-form-checkbox
      v-model="copyUserPermissions"
      @input="copyChanged($event)"
    >
      {{ $t('user-manager.editing.permissions.copy-user') }}
    </b-form-checkbox>

    <b-row>
      <b-col
        sm="12"
        md="8"
        lg="4"
      >
        <b-form-group
          class="mt-2"
          id="userListFieldSet"
          label-for="userList"
          :label="$t('user-manager.editing.permissions.user')"
          :label-class="!copyUserPermissions ? 'disabled' : ''"
        >
          <s-select
            v-model="selectedUserToCopy"
            id="userList"
            value-field="id"
            text-field="name"
            :disabled="!copyUserPermissions"
            :options="usersToCopy"
            @change="tryApplySelectedUserPermissions()"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-form-group
      :label="$t('user-manager.editing.permissions.select-permission-level')"
      label-for="permissionLevels"
    >
      <b-form-radio-group
        id="permissionLevels"
        v-model="user.level"
        class="permissions__levels-radios"
        @input="selectedLevelChanged()"
      >
        <b-form-radio
          v-for="levelKey in permissionLevelKeys"
          :disabled="!canSelectLevel(levelKey)"
          :key="levelKey"
          :value="levelKey"
        >
          {{ $t(`permissions.levels.${levelKey}`) }}
        </b-form-radio>
      </b-form-radio-group>
    </b-form-group>
    <div>
      <permission-groups
        :user="user"
        :groups="permissionGroups || []"
        :is-disabled="canDisableGroup()"
      />
    </div>
  </div>
</template>
<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

.permissions
  &__group
    break-inside: avoid-column
    > div > *
      display: block
    &__levels-radios > *
      display: block
    +media-min-sm()
    +media-min-md()
      &__levels-radios > *
        display: inline-flex
    +media-min-lg()
      &__groups
        column-count: 2
</style>

<script>
import { mapGetters } from 'vuex';
import { level } from '@/domain/enums/user';
import UserService from '@/services/user';
import PermissionService from '@/domain/services/generalPanel/adminPanel/userPermissionsService';
import PermissionGroups from '@/domain/components/generalPanel/adminPanel/user/editing/PermissionGroups';

export default {
  components: {
    PermissionGroups,
  },
  props: {
    /**
     * Instância do usuário.
     * Interface esperada:
     * {
     *   level: 'string',
     *   permissions: { PER001: true, PER002: false, ...},
     *   ...
     * }
     *
     * Existem métodos utilitários em PermissionsService que convertem entre
     * um array de permissions e a interface de objeto de permissões
     * utilizada aqui. Ver PermissionsService.toPermissionObject(...) e
     * PermissionsService.toPermissionArray(...) para maiores detalhes.
     */
    user: Object,
  },
  data() {
    return {
      usersToCopy: [],
      permissionLevels: null,
      permissionGroups: null,
      copyUserPermissions: false,
      selectedUserToCopy: null,
      changingPermissions: false,
    };
  },
  computed: {
    ...mapGetters(['isSuperAdmin']),
    selectedPermissionLevel() {
      if (!this.permissionLevels) {
        return null;
      }
      return this.permissionLevels[this.user.level];
    },
    permissionLevelKeys() {
      if (this.permissionLevels !== null) {
        return Object.keys(this.permissionLevels);
      }
      return [];
    },
  },
  created() {
    this.initializeValues();
  },
  methods: {
    getGroupedPermissions() {
      PermissionService.getGroupedPermissions().then((groups) => {
        this.permissionGroups = groups;
      });
    },
    getPermissionLevels() {
      PermissionService.getPermissionLevels().then((levels) => {
        this.permissionLevels = levels;
      });
    },
    getUsers() {
      PermissionService.getUsersToCopy().then((users) => {
        const nullOption = { name: this.$t('general.select'), id: null };
        this.usersToCopy = [nullOption, ...users];
      });
    },
    initializeValues() {
      if (!this.user.permissions) {
        this.user.permissions = {};
      }
      this.getGroupedPermissions();
      this.getPermissionLevels();
      this.getUsers();
    },
    copyChanged(newValue) {
      if (!newValue) {
        this.selectedUserToCopy = null;
        return;
      }
      this.tryTakePermissionsToChange(this.copyChangedCallback);
    },
    copyChangedCallback() {
      Object.keys(this.user.permissions).forEach((permission) => {
        if (this.canChangePermission(permission)) {
          this.$set(this.user.permissions, permission, false);
        }
      });
      this.$set(this.user, 'level', level.Custom);
    },
    canChangePermission(permission) {
      return UserService.hasPermission(permission);
    },
    tryApplySelectedUserPermissions() {
      if (this.selectedUserToCopy) {
        this.tryTakePermissionsToChange(
          this.tryApplySelectedUserPermissionsCallback,
        );
      }
    },
    async tryApplySelectedUserPermissionsCallback() {
      const allApplied = await PermissionService.applyUserPermissions(
        this.selectedUserToCopy,
        this.user.permissions,
      );

      this.$set(this.user, 'permissions', {
        ...this.user.permissions,
      });

      this.$set(
        this.user,
        'level',
        PermissionService.getLevelByPermissions(
          this.user.permissions,
          this.permissionLevels,
        ),
      );

      if (!allApplied) {
        this.clearUserToCopy();
        this.$pushAlert({ code: 'ma013' });
      }
    },
    clearUserToCopy() {
      this.selectedUserToCopy = null;
      this.copyUserPermissions = false;
    },
    selectedLevelChanged() {
      this.tryTakePermissionsToChange(this.selectedLevelChangedCallback);
    },
    async selectedLevelChangedCallback() {
      this.clearUserToCopy();
      const allApplied = await PermissionService.applyLevelPermissions(
        this.selectedPermissionLevel,
        this.user.permissions,
      );

      this.$set(this.user, 'permissions', {
        ...this.user.permissions,
      });

      if (!allApplied) {
        this.$set(this.user, 'level', level.Custom);
        this.$pushAlert({
          message: this.$t(
            'user-manager.editing.permissions.level-cant-be-applied',
          ),
          type: 'warning',
        });
      }
    },
    async tryTakePermissionsToChange(callback) {
      if (this.changingPermissions) {
        return;
      }
      this.changingPermissions = true;
      try {
        await callback();
      } finally {
        this.changingPermissions = false;
      }
    },
    canSelectLevel(levelName) {
      return levelName !== level.SuperAdmin || this.isSuperAdmin;
    },
    canDisableGroup() {
      return this.user.level !== level.Custom;
    },
  },
};
</script>
